/**
 * ClientInsightsSummaryCard Component
 * Displays AI-generated client overview summary with refresh functionality
 *
 * Pattern: Adapted from SummaryCard.tsx for client insights
 * Features:
 * - Purple gradient background (AI theme)
 * - AI-generated summary text
 * - Refresh button with loading spinner
 * - Confidence score badge
 * - "Last updated" timestamp
 */

import React from 'react';
import { Sparkles, RefreshCw } from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';

interface ClientInsightsSummaryCardProps {
  summary: string;
  lastUpdated: string; // ISO8601 timestamp
  confidenceScore?: number; // 0.0-1.0
  onRefresh: () => void;
  isRefreshing: boolean;
}

export const ClientInsightsSummaryCard: React.FC<ClientInsightsSummaryCardProps> = ({
  summary,
  lastUpdated,
  confidenceScore,
  onRefresh,
  isRefreshing,
}) => {
  return (
    <div className="bg-gradient-to-br from-purple-50 to-blue-50 border border-purple-200 rounded-lg p-6 shadow-sm">
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <Sparkles className="w-6 h-6 text-purple-600" aria-hidden="true" />
          <h3 className="text-lg font-semibold text-purple-900">AI Client Overview</h3>
        </div>

        <div className="flex items-center gap-3">
          {/* Confidence Score Badge */}
          {confidenceScore !== undefined && (
            <span
              className="text-xs bg-purple-100 text-purple-700 px-2 py-1 rounded font-medium"
              title={`AI confidence score: ${(confidenceScore * 100).toFixed(0)}%`}
            >
              {Math.round(confidenceScore * 100)}% Confidence
            </span>
          )}

          {/* Refresh Button */}
          <button
            onClick={onRefresh}
            disabled={isRefreshing}
            className="p-2 hover:bg-purple-100 rounded-md transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            title="Refresh insights"
            aria-label="Refresh insights"
          >
            <RefreshCw
              className={`w-4 h-4 text-purple-600 ${isRefreshing ? 'animate-spin' : ''}`}
            />
          </button>
        </div>
      </div>

      {/* Summary Text */}
      <p className="text-gray-700 leading-relaxed mb-3">{summary}</p>

      {/* Last Updated Timestamp */}
      <p className="text-xs text-gray-600">
        Last updated:{' '}
        {formatDistanceToNow(new Date(lastUpdated), { addSuffix: true })}
      </p>
    </div>
  );
};

export default ClientInsightsSummaryCard;
